import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, combineLatest, concat, debounceTime, distinctUntilChanged, forkJoin, from, map, mergeMap, Observable, of, retry, shareReplay, switchMap, tap, } from 'rxjs';
import { environment } from 'src/environments/environment';
import { v4 as uuidv4 } from 'uuid';
import { OrganizationService } from 'src/app/@shared/services/organization.service';
import { OdataResponse, Version, Store } from '..';
import { DEFAULT_PAGING } from 'src/app/@shared/constants/site.constants';
import { VersionAttributeFilterSelection, VersionAttributeFilterPair, VersionAttributeSearchApiItem, VersionAttributeSearchResult, mapVersionsToAttributeSearchResults } from '../models/store-attribute.model';

export const VERSIONS_PAGING = {
  previousPageIndex: 0,
  pageIndex: 0,
  pageSize: 10,
  length: 0,
};

export const STORES_PAGING = {
  previousPageIndex: 0,
  pageIndex: 0,
  pageSize: 10,
  length: 0,
  active: 'StoreName',
  direction: 'asc'
};

export const VERSION_STORE_PAGE_SIZE = 50;
//export const VERSION_STORE_LOAD_ALL_CHUNK = 500;

export function normalizeODataCollection<T>(res: unknown): { items: T[]; total: number } {
  if (Array.isArray(res)) {
    return { items: res, total: res.length };
  }

  const items = (res as { value?: T[] })?.value ?? [];
  const total = (res as { '@odata.count'?: number })?.['@odata.count'] ?? items.length;
  return { items, total };
}

export function extractApiErrorMessage(error: unknown, fallback = 'Search failed'): string {
  if (!(error instanceof HttpErrorResponse)) {
    return fallback;
  }

  const body = error.error;
  if (typeof body === 'string' && body.trim()) {
    return body;
  }

  if (Array.isArray(body) && body.length) {
    const first = body[0];
    if (typeof first === 'string') {
      return first;
    }
    if (first && typeof first === 'object') {
      const message =
        (first as { errorMessage?: string }).errorMessage ??
        (first as { ErrorMessage?: string }).ErrorMessage;
      if (message) {
        return message;
      }
    }
  }

  if (body && typeof body === 'object') {
    const message =
      (body as { message?: string }).message ??
      (body as { Message?: string }).Message;
    if (message) {
      return message;
    }
  }

  return fallback;
}

@Injectable({
  providedIn: 'root',
})

export class VersionService<TVersion extends Version> {

  // initialize behavior subjects
  private eventIdBehaviorSubject = new BehaviorSubject<string>('');
  private loadingBehaviorSubject = new BehaviorSubject<boolean>(false);
  private pageBehaviorSubject = new BehaviorSubject(VERSIONS_PAGING);
  private sortBehaviorSubject = new BehaviorSubject({ active: 'VersionName', direction: 'asc', });
  private searchBehaviorSubject = new BehaviorSubject<string>('');
  private uploadVersionsPageBehaviorSubject = new BehaviorSubject(DEFAULT_PAGING);
  private uploadVersionsSortBehaviorSubject = new BehaviorSubject({ active: 'VersionName', direction: 'asc', });
  private uploadVersionsSearchBehaviorSubject = new BehaviorSubject<string>('');
  private reloadBehaviorSubject = new BehaviorSubject<string>('');
  private offerGroupVersionPageBehaviorSubject = new BehaviorSubject(DEFAULT_PAGING);
  private offerGroupVersionsSearchBehaviorSubject = new BehaviorSubject('');
  private offerGroupVersionsSortBehaviorSubject = new BehaviorSubject({ active: 'VersionName', direction: 'asc', });
  private offerGroupVersionsreloadBehaviorSubject = new BehaviorSubject<string>('');
  private versionDialogPageBehaviorSubject = new BehaviorSubject(DEFAULT_PAGING);
  private versionDialogSearchBehaviorSubject = new BehaviorSubject('');
  private versionDialogSortBehaviorSubject = new BehaviorSubject({ active: 'VersionName', direction: 'asc', });
  private versionDialogReloadBehaviorSubject = new BehaviorSubject<string>('');
  private versionDialogSelectedKeysBehaviorSubject = new BehaviorSubject<string[] | null>(null);
  private attributeFiltersBehaviorSubject = new BehaviorSubject<VersionAttributeFilterSelection[]>([]);
  private attributeFilterPairsBehaviorSubject = new BehaviorSubject<VersionAttributeFilterPair[]>([]);
  private attributeSearchLoadingBehaviorSubject = new BehaviorSubject<boolean>(false);
  private attributeSearchErrorBehaviorSubject = new BehaviorSubject<string | null>(null);


  // we do not wish to expose our behavior subjects.  create public observables
  public isLoading$ = this.loadingBehaviorSubject.asObservable();
  public sort$ = this.sortBehaviorSubject.asObservable();
  public page$ = this.pageBehaviorSubject.asObservable();
  public search$ = this.searchBehaviorSubject.asObservable();
  public uploadVersionsSort$ = this.uploadVersionsSortBehaviorSubject.asObservable();
  public uploadVersionsPage$ = this.uploadVersionsPageBehaviorSubject.asObservable();
  public uploadVersionsSearch$ = this.uploadVersionsSearchBehaviorSubject.asObservable();
  public offerGroupVersionsPage$ = this.offerGroupVersionPageBehaviorSubject.asObservable();
  public offerGroupVersionsSearch$ = this.offerGroupVersionsSearchBehaviorSubject.asObservable();
  public offerGroupVersionsSort$ = this.offerGroupVersionsSortBehaviorSubject.asObservable();
  public versionDialogPage$ = this.versionDialogPageBehaviorSubject.asObservable();
  public versionDialogSearch$ = this.versionDialogSearchBehaviorSubject.asObservable();
  public versionDialogSort$ = this.versionDialogSortBehaviorSubject.asObservable();
  public versionDialogSelectedKeys$ = this.versionDialogSelectedKeysBehaviorSubject.asObservable();
  public attributeFilters$ = this.attributeFiltersBehaviorSubject.asObservable();
  public attributeSearchLoading$ = this.attributeSearchLoadingBehaviorSubject.asObservable();
  public attributeSearchError$ = this.attributeSearchErrorBehaviorSubject.asObservable();

  // create the parameters observable that looks for changes in page, startDate, endDate, etc
  public params$ = combineLatest([
    this.eventIdBehaviorSubject,
    this.pageBehaviorSubject, // add debounce if we need to wait for user input ex: .pipe(debounceTime(300)),
    this.sortBehaviorSubject,
    this.searchBehaviorSubject.pipe(debounceTime(300)),
    this.reloadBehaviorSubject,
  ]).pipe(
    distinctUntilChanged((previous, current) => {
      // if the values coming down this pipe are the same, don't continue the pipe
      return JSON.stringify(previous) === JSON.stringify(current);
    }),
    map(([eventId, page, sort, search, reload]) => {

      // set the query string odata parameters
      let params: HttpParams = new HttpParams({
        fromObject: {
          eventId: this.eventIdBehaviorSubject.value,
          $skip: page.pageIndex * page.pageSize,
          $top: page.pageSize,
          $orderby: `${sort.active} ${sort.direction}`,
          $count: true,
        }
      });

      // if there is a search, add the search to the parameters
      if (search.length) {
        params = params.append('$search', `"${search}"`);
      }

      return params;
    })
  );

  // create the parameters observable that looks for changes in page, startDate, endDate, etc
  public offerGroupParams$ = combineLatest([
    this.eventIdBehaviorSubject,
    this.offerGroupVersionPageBehaviorSubject, // add debounce if we need to wait for user input ex: .pipe(debounceTime(300)),
    this.offerGroupVersionsSortBehaviorSubject,
    this.offerGroupVersionsSearchBehaviorSubject.pipe(debounceTime(300)),
    this.offerGroupVersionsreloadBehaviorSubject,
  ]).pipe(
    distinctUntilChanged((previous, current) => {
      // if the values coming down this pipe are the same, don't continue the pipe
      return JSON.stringify(previous) === JSON.stringify(current);
    }),
    map(([eventId, page, sort, search, reload]) => {

      // set the query string odata parameters
      let params: HttpParams = new HttpParams({
        fromObject: {
          eventId: this.eventIdBehaviorSubject.value,
          $skip: page.pageIndex * page.pageSize,
          $top: page.pageSize,
          $orderby: `${sort.active} ${sort.direction}`,
          $count: true,
        }
      });

      // if there is a search, add the search to the parameters
      if (search.length) {
        params = params.append('$search', `"${search}"`);
      }

      return params;
    })
  );

  // create the parameters observable that looks for changes in page, startDate, endDate, etc
  public uploadVersionsParams$ = combineLatest([
    this.eventIdBehaviorSubject,
    this.uploadVersionsPageBehaviorSubject, // add debounce if we need to wait for user input ex: .pipe(debounceTime(300)),
    this.uploadVersionsSortBehaviorSubject,
    this.uploadVersionsSearchBehaviorSubject.pipe(debounceTime(300)),
    this.reloadBehaviorSubject,
  ]).pipe(
    distinctUntilChanged((previous, current) => {
      // if the values coming down this pipe are the same, don't continue the pipe
      return JSON.stringify(previous) === JSON.stringify(current);
    }),
    map(([eventId, page, sort, search, reload]) => {

      // set the query string odata parameters
      let params: HttpParams = new HttpParams({
        fromObject: {
          eventId: this.eventIdBehaviorSubject.value,
          $skip: page.pageIndex * page.pageSize,
          $top: page.pageSize,
          $orderby: `${sort.active} ${sort.direction}`,
          $count: true,
        }
      });

      // if there is a search, add the search to the parameters
      if (search.length) {
        params = params.append('$search', `"${search}"`);
      }

      return params;
    })
  );

  // create the versions observable that calls http get when any of our parameters change
  private versionResponse$ = this.params$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)), // set isLoading to true
    switchMap((_params) => {
      const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Versions/ByEventId`;
      return this.httpClient.get(url, { params: _params });
    }),
    tap(() => this.loadingBehaviorSubject.next(false)), // set isLoading to false
    shareReplay(1) // make sure all subscriptions share the same http call (otherwise there will be a http call for each subscription)
  );

  // create the versions observable that calls http get when any of our parameters change
  private uploadVersionResponse$ = this.uploadVersionsParams$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)), // set isLoading to true
    switchMap((_params) => {
      const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Versions/ByEventId`;
      return this.httpClient.get(url, { params: _params });
    }),
    tap(() => this.loadingBehaviorSubject.next(false)), // set isLoading to false
    shareReplay(1) // make sure all subscriptions share the same http call (otherwise there will be a http call for each subscription)
  );

  // create the versions observable that calls http get when any of our parameters change
  private offerGroupVersionResponse$ = this.offerGroupParams$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)), // set isLoading to true
    switchMap((_params) => {
      const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Versions/ByEventId`;
      return this.httpClient.get(url, { params: _params });
    }),
    tap(() => this.loadingBehaviorSubject.next(false)), // set isLoading to false
    shareReplay(1) // make sure all subscriptions share the same http call (otherwise there will be a http call for each subscription)
  );

  // version listing
  public offerGroupVersions$: Observable<TVersion[]> = this.offerGroupVersionResponse$.pipe(
    map((res: any) => res.value)
  );

  // total number of version records based on filtering
  public offerGroupVersionstotalRecords$: Observable<number> = this.offerGroupVersionResponse$.pipe(
    map((res: any) => res['@odata.count'])
  );

  private allVersionsResponse$ = this.params$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)), // set isLoading to true
    switchMap((_params) => {
      const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Versions/AllVersionByEventId`;
      return this.httpClient.get(url, { params: { eventId: this.eventIdBehaviorSubject.value } });
    }),
    tap(() => this.loadingBehaviorSubject.next(false)), // set isLoading to false
    shareReplay(1) // make sure all subscriptions share the same http call (otherwise there will be a http call for each subscription)
  );

  // version listing
  public allVersions$: Observable<TVersion[]> = this.allVersionsResponse$.pipe(
    map((res: any) => res.value)
  );

  getVersionsForVariants(eventId: string): Observable<TVersion[]> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Versions/AllVersionByEventId?eventId=${eventId}`;
    return this.httpClient.get<TVersion[]>(url).pipe(map((res: any) => res.value));
  }

  getVersionsByEventId(eventId: string, top = 500): Observable<TVersion[]> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Versions/ByEventId`;
    const params = new HttpParams({
      fromObject: {
        eventId,
        $skip: 0,
        $top: top,
        $orderby: 'VersionName asc',
        $count: true,
      },
    });
    return this.httpClient.get<any>(url, { params }).pipe(
      map((res: any) => res?.value ?? []),
    );
  }

  // version listing
  public versions$: Observable<TVersion[]> = this.versionResponse$.pipe(
    map((res: any) => res.value)
  );

  // total number of version records based on filtering
  public totalRecords$: Observable<number> = this.versionResponse$.pipe(
    map((res: any) => res['@odata.count'])
  );

  // version listing
  public uploadVersions$: Observable<TVersion[]> = this.uploadVersionResponse$.pipe(
    map((res: any) => res.value)
  );

  // total number of version records based on filtering
  public uploadVersionstotalRecords$: Observable<number> = this.uploadVersionResponse$.pipe(
    map((res: any) => res['@odata.count'])
  );

  constructor(private httpClient: HttpClient, private organizationService: OrganizationService) { }

  set eventId(eventId: string) {
    this.eventIdBehaviorSubject.next(eventId);
  }

  get eventId() {
    return this.eventIdBehaviorSubject.value;
  }

  // set the current page
  page(page: any) {
    this.pageBehaviorSubject.next(page);
  }

  resetPage() {
    this.pageBehaviorSubject.next(DEFAULT_PAGING);
  }

  // sets the sort property and order
  sort(sort: any) {
    this.sortBehaviorSubject.next(sort);
  }

  resetSort() {
    this.sortBehaviorSubject.next({ active: 'VersionName', direction: 'asc', });
  }

  // sets the search phrase
  search(search: string) {
    const page = this.pageBehaviorSubject.value;
    page.pageIndex = 0;
    page.previousPageIndex = 0;
    this.searchBehaviorSubject.next(search);
    this.pageBehaviorSubject.next(page);
  }

  // set the current page
  uploadVersionsPage(page: any) {
    this.uploadVersionsPageBehaviorSubject.next(page);
  }

  // sets the sort property and order
  uploadVersionsSort(sort: any) {
    this.uploadVersionsSortBehaviorSubject.next(sort);
  }

  // sets the search phrase
  uploadVersionsSearch(search: string) {
    const page = this.uploadVersionsPageBehaviorSubject.value;
    page.pageIndex = 0;
    page.previousPageIndex = 0;
    this.uploadVersionsSearchBehaviorSubject.next(search);
    this.uploadVersionsPageBehaviorSubject.next(page);
  }

  offerGroupVersionsPage(page: any) {
    this.offerGroupVersionPageBehaviorSubject.next(page);
  }

  resetOfferGroupVersionsPage() {
    const page = this.offerGroupVersionPageBehaviorSubject.value;
    page.pageIndex = 0;
    page.previousPageIndex = 0;
    this.offerGroupVersionPageBehaviorSubject.next(page);
  }

  // sets the sort property and order
  offerGroupVersionsSort(sort: any) {
    this.offerGroupVersionsSortBehaviorSubject.next(sort);
  }

  // sets the search phrase
  offerGroupVersionsSearch(search: string) {
    const page = this.offerGroupVersionPageBehaviorSubject.value;
    page.pageIndex = 0;
    page.previousPageIndex = 0;
    this.offerGroupVersionsSearchBehaviorSubject.next(search);
    this.offerGroupVersionPageBehaviorSubject.next(page);
  }

  // reloads/refreshes the version listing
  offerGroupVersionsReload() {
    // reload the event data
    this.offerGroupVersionsreloadBehaviorSubject.next(uuidv4());
  }

  // reloads/refreshes the version listing
  reload() {
    // reload the event data
    this.reloadBehaviorSubject.next(uuidv4());
  }

  // gets an version by id
  getVersion(versionId: string): Observable<TVersion> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Versions/${versionId}`;
    return this.httpClient.get<TVersion>(url);
  }

  // up-serts versions
  saveVersion(eventId: string, versionIdForOffers: string | null, version: TVersion): Observable<TVersion> {
    if (!version.Id || version.Id === '0') {
      // create new record
      version.Id = '';

      let url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Versions/AddToEvent?eventId=${eventId}`;

      if (versionIdForOffers) {
        url += `&versionIdForOffers=${versionIdForOffers}`;
      }

      return this.httpClient.post<TVersion>(url, version);
    } else {
      // edit existing record
      const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Versions/${version.Id}?eventId=${eventId}`;
      return this.httpClient.put<TVersion>(url, version);
    }
  }
  // deletes an version by id
  deleteVersion(id: string) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Versions/${id}`;
    return this.httpClient.delete(url);
  }

  deleteVersions(versions: any, versionIdForStores: string | null) {
    let url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Versions/DeleteVersions`;
    const versionIds: string[] = versions.map((v: { Id: string }) => v.Id);
    if (versionIdForStores) {
      url += `?versionIdForStores=${versionIdForStores}`;
    }
    return this.httpClient.post(url, versionIds);
  }

  prepareVersionStringtoDelete(versions: any) {
    let urlParams = '';
    if (versions && versions.length > 0) {
      for (let index = 0; index <= versions.length - 1; index++) {
        urlParams += `versionIds=${versions[index]?.Id}`;
        if (index != versions.length - 1) {
          urlParams += '&'
        }
      }
    }
    return urlParams;
  }

  // gets the distinct list of version names for the organization
  ///TODO: at some point the list of names could get large and we may need to apply an odata search to this function
  getDistinctVersionNames(): Observable<OdataResponse> {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Versions/DistinctNames`;
    return this.httpClient.get<OdataResponse>(url);
  }

  public versionNames$: Observable<Version[]> = this.getDistinctVersionNames().pipe(
    map((res: any) => res.value)
  );

  downloadVersions(eventId: string) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Events/ExportVersions?eventId=${eventId}`;
    window.open(url, '_blank');
  }

  downloadVersionStores(eventId: string) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Events/ExportVersionStores?eventId=${eventId}`;
    window.open(url, '_blank');
  }

  downloadVersionStoreSummary(eventId: string, versionIds: string[] = []) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Events/ExportVersionStoreSummary?eventId=${eventId}`;
    return this.httpClient.post(url, versionIds, { responseType: 'blob', observe: 'response' });
  }

  applyVersionCodes(versions: any, body: any) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/PromoDomains/ApplyVersionCodes`;

    const requestBody = {
      offerPromoCopies: body,
      versionIds: this.prepareVersionIdsToArray(versions)
    };
    return this.httpClient.post(url, requestBody);
  }

  prepareVersionIdsToArray(versions: any[]): string[] {
    return versions?.map(v => v?.Id).filter(id => !!id) ?? [];
  }

  public versionsWithStores$: Observable<TVersion[]> = this.versionResponse$.pipe(
    map((res: any) =>
      (res.value ?? []).map((v: any) => ({
        Id: v.Id,
        VersionName: v.VersionName,
        ClientKey: v.ClientKey ?? '',
        Disclaimer: v.Disclaimer ?? '',
        stores: [],
        storesCount: 0,
        storePageIndex: 0,
        selected: false,
        isLoading: true,
        hasError: false,
      })) as TVersion[]
    ),
    switchMap((versions: TVersion[]) => {
      if (!versions.length) return of([]);

      return concat(
        of(versions),
        from(versions).pipe(
          retry(2),
          mergeMap(
            v =>
              this.getStoresByVersionId(v.Id).pipe(
                map(({ stores, count }) => {
                  v.stores = stores;
                  v.storesCount = count;
                  v.isLoading = false;
                  v.hasError = false;
                  return [...versions];
                }),
                catchError(err => {
                  console.error(`Failed to load version ${v.VersionName}`, err);
                  v.isLoading = false;
                  v.hasError = true;
                  v.stores = [];
                  v.storesCount = 0;
                  return of([...versions]);
                })
              ), 2 // <= concurrency limit
          )
        )
      );
    }),
    shareReplay(1)
  );

  public getStoresByVersionId(versionId: string, pageIndex = 0, pageSize = VERSION_STORE_PAGE_SIZE): Observable<{ stores: Store[], count: number }> {
    if (!versionId) return of({ stores: [], count: 0 });

    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Stores/ByVersionId`;
    let queryParams = new HttpParams()
      .set('versionId', versionId)
      .set('$skip', String(pageIndex * pageSize))
      .set('$top', String(pageSize))
      .set('$orderby', `${STORES_PAGING.active} ${STORES_PAGING.direction}`)
      .set('$count', 'true');

    return this.httpClient.get<{ value: any[], '@odata.count': number }>(url, { params: queryParams }).pipe(
      map(res => ({
        stores: (res?.value ?? []).map(s => ({ Id: s.Id, StoreName: s.StoreName })),
        count: res['@odata.count'] ?? 0
      })),
      catchError(err => {
        console.error(`Error fetching stores for versionId ${versionId}:`, err);
        return of({ stores: [], count: 0 });
      })
    );
  }

  // public getAllStoresByVersionId(versionId: string, total: number): Observable<{ stores: Store[], count: number }> {
  //   if (!versionId || total <= 0) {
  //     return of({ stores: [], count: 0 });
  //   }

  //   if (total <= VERSION_STORE_LOAD_ALL_CHUNK) {
  //     return this.getStoresByVersionId(versionId, 0, total);
  //   }

  //   const pageCount = Math.ceil(total / VERSION_STORE_LOAD_ALL_CHUNK);
  //   const requests = Array.from({ length: pageCount }, (_, pageIndex) =>
  //     this.getStoresByVersionId(versionId, pageIndex, VERSION_STORE_LOAD_ALL_CHUNK)
  //   );

  //   return forkJoin(requests).pipe(
  //     map(results => ({
  //       stores: results.flatMap(r => r.stores),
  //       count: results[0]?.count ?? total,
  //     })),
  //     catchError(err => {
  //       console.error(`Error fetching all stores for versionId ${versionId}:`, err);
  //       throw err;
  //     })
  //   );
  // }

  // Bulk Operations - Versions Add & Remove logic
  versionDialogPage(page: any) {
    this.versionDialogPageBehaviorSubject.next(page);
  }

  resetVersionDialogPage() {
    const page = this.versionDialogPageBehaviorSubject.value;
    page.pageIndex = 0;
    page.previousPageIndex = 0;
    this.versionDialogPageBehaviorSubject.next(page);
  }

  versionDialogSort(sort: any) {
    this.versionDialogSortBehaviorSubject.next(sort);
  }

  versionDialogSearch(search: string) {
    const page = this.versionDialogPageBehaviorSubject.value;
    page.pageIndex = 0;
    page.previousPageIndex = 0;
    this.versionDialogSearchBehaviorSubject.next(search);
    this.versionDialogPageBehaviorSubject.next(page);
  }

  versionDialogReload() {
    this.versionDialogReloadBehaviorSubject.next(uuidv4());
  }

  setVersionDialogKeys(keys: string[] | null) {
    if (keys && keys.length === 0) {
      this.versionDialogSelectedKeysBehaviorSubject.next(null);
    } else {
      this.versionDialogSelectedKeysBehaviorSubject.next(keys);
    }
  }

  setAttributeFilters(filters: VersionAttributeFilterSelection[]): void {
    this.attributeFiltersBehaviorSubject.next(filters);
  }

  setAttributeFilterPairs(pairs: VersionAttributeFilterPair[]): void {
    this.attributeFilterPairsBehaviorSubject.next(pairs);
  }

  private attributeSearchParams$ = combineLatest([
    this.eventIdBehaviorSubject,
    this.attributeFilterPairsBehaviorSubject,
    this.reloadBehaviorSubject,
  ]).pipe(
    distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
  );

  private attributeSearchResponse$ = this.attributeSearchParams$.pipe(
    tap(() => {
      this.attributeSearchLoadingBehaviorSubject.next(true);
      this.attributeSearchErrorBehaviorSubject.next(null);
    }),
    switchMap(([eventId, attributeFilters, _reload]) => {
      if (!eventId || !attributeFilters.length) {
        this.attributeSearchLoadingBehaviorSubject.next(false);
        return of({ results: [] as VersionAttributeSearchResult[], total: 0 });
      }

      const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Versions/ByEventIdWithAttributeFilter`;
      const body = {
        eventId,
        filterModel: {
          search: '',
          skip: 0,
          limit: 500,
          orderDirection: 'asc',
          orderBy: 'VersionName',
        },
        attributeFilters: attributeFilters.map((filter) => ({
          attributeName: filter.attributeName,
          valueSourceField: filter.valueSourceField,
          value: filter.value,
        })),
      };

      return this.httpClient.post<any>(url, body).pipe(
        map((res) => {
          const { items, total } = normalizeODataCollection<VersionAttributeSearchApiItem>(res);
          return {
            results: mapVersionsToAttributeSearchResults(items),
            total,
          };
        }),
        catchError((error) => {
          this.attributeSearchErrorBehaviorSubject.next(
            extractApiErrorMessage(error, 'Error searching versions by attribute filters'),
          );
          return of({ results: [] as VersionAttributeSearchResult[], total: 0 });
        }),
      );
    }),
    tap(() => this.attributeSearchLoadingBehaviorSubject.next(false)),
    shareReplay(1),
  );

  public attributeSearchResults$: Observable<VersionAttributeSearchResult[]> =
    this.attributeSearchResponse$.pipe(map((res) => res.results));

  public attributeSearchTotal$: Observable<number> =
    this.attributeSearchResponse$.pipe(map((res) => res.total));

  clearVersionDialogKeys() {
    this.versionDialogSelectedKeysBehaviorSubject.next(null);
  }

  public versionDialogParams$ = combineLatest([
    this.eventIdBehaviorSubject,
    this.versionDialogPageBehaviorSubject,
    this.versionDialogSortBehaviorSubject,
    this.versionDialogSearchBehaviorSubject.pipe(debounceTime(300)),
    this.versionDialogReloadBehaviorSubject,
    this.versionDialogSelectedKeysBehaviorSubject
  ]).pipe(
    distinctUntilChanged((previous, current) => {
      return JSON.stringify(previous) === JSON.stringify(current);
    }),
    map(([eventId, page, sort, search, reload, selectedKeys]) => {

      let params = new HttpParams().set('eventId', eventId);
      params = params
        .set('limit', page.pageSize)
        .set('skip', page.pageIndex * page.pageSize)
        .set('orderBy', sort.active)
        .set('orderDirection', sort.direction || 'asc');

      if (search?.length) {
        params = params.set('search', `"${search}"`);
      }

      return {
        params,
        body: selectedKeys ?? []
      };
    })
  );

  private versionDialogResponse$ = this.versionDialogParams$.pipe(
    tap(() => this.loadingBehaviorSubject.next(true)),
    switchMap(({ params, body }) => {
      const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Versions/SearchByOffers`;
      return this.httpClient.post<any>(url, body ?? [], { params }
      );
    }),
    tap(() => this.loadingBehaviorSubject.next(false)),
    shareReplay(1)
  );

  public versionDialogVersions$: Observable<TVersion[]> =
    this.versionDialogResponse$.pipe(
      map(res => res.Items ?? [])
    );

  public versionDialogTotalRecords$: Observable<number> =
    this.versionDialogResponse$.pipe(
      map(res => res.Count ?? 0)
    );

  disAssociateVersionsByOffers(versions: any, offers: any, eventId: string) {
    const url = `${environment.pr1ApiUrl}/${this.organizationService.organization?.apiPath}/${this.organizationService.organization?.version}/Versions/DisAssociateVersionsByOffers?eventId=${eventId}`;
    const requestBody = {
      eventId: eventId,
      versionIds: this.prepareIdsToArray(versions),
      offerIds : offers,
    };
    return this.httpClient.post(url, requestBody);
  }

  prepareIdsToArray(items: any[]): string[] {
    return items?.map(v => v?.Id).filter(id => !!id) ?? [];
  }

}
